import { AddOnServiceViewModel } from '@/shared/models/addOnService';
import { AgencyFeesPropertyModel } from '@/shared/models/agencyFee';
import { AmenityModel } from '@/shared/models/amenities';
import { OfficialRatesExportType } from '@/shared/models/enums.type';
import { isNumber } from 'lodash/fp';

export class PropertyFormModel {
  id = 0;
  name = "";
  typeId = 0;
  categoryId = 1;
  keyPickupLocationStreetName = "";
  keyPickupLocationHouseNumber = "";
  keyPickupLocationPostalCodeId: number | null = null;
  keyPickupLocationCityId: number | null = null;
  keyPickupLocationLatitude: number | null = null;
  keyPickupLocationLongitude: number | null = null;
  streetName = "";
  houseNumber = "";
  postalCodeId: number | null = null;
  cityId: number | null = null;
  countryId = 1;
  latitude: number | null = null;
  longitude: number | null = null;
  ownerId: number | null = null;
  propertyManagerId: number | null = null;
  pricingModelId = 2; // ? https://dev.azure.com/infranet/Development/_sprints/taskboard/Development%20Team/Development/Sprint%2041?workitem=2624
  smokingAllowed = false;
  petsAllowed = false;
  childrenAllowed = true;
  partiesAllowed = false;
  checkInTimeFrom = "";
  checkInTimeUntil = "";
  checkOutTimeFrom = "";
  checkOutTimeUntil = "";
  quietTime1From: string | null = null;
  quietTime1Until: string | null = null;
  quietTime2From: string | null = null;
  quietTime2Until: string | null = null;
  cutOffTime: string | null = null;
  cutOffDays: number | null = null;
  acceptsOnlyCash = false;
  paysVat = false;
  paysCityTax = false;
  sortOrderOnBookingEngine = 1;
  ownerPriceOffset = 0;
  webUrl = "";
  updateReservations = false; // used if user changes agency fee amount on property 'owner' page and decides to save property - it will update future reservations with the selected agency fees

  ownerDoesCleaning = false;
  ownerDoesCheckin = false;
  ownerDoesCheckout = false;
  ownerPricingModelId: number | null = null;
  ownerPayoutPercent: number | null = null;

  agencyWorkModelId: number | null = null;
  vatCategoryForPrepaymentsId: number | null = null;
  agencyCommissionPercentForPrepayments: number | null = 0;

  categorizationClass: string | null = null;
  categorizationNumber: string | null = null;
  categorizationDate?: Date;

  defaultBusinessUnitId: number | null = null;

  addOnServices: AddOnServiceViewModel[] = [];
  poiIds: number[] = [];
  cardTypeIds: number[] = [];
  languageIds: number[] = [];
  amenities: AmenityModel[] = [];
  agencyFeesProperty: AgencyFeesPropertyModel[] = [];

  get location() {
    const res = { lat: this.latitude, lng: this.longitude };
    return isNumber(res.lat) && isNumber(res.lng) ? res as LatLng : null;
  }

  set location(val: LatLng | null) {
    if (val) {
      this.latitude = val.lat;
      this.longitude = val.lng;
    } else {
      this.latitude = null;
      this.longitude = null;
    }
  }
  
  get keyPickupLocation() {
    const res = { lat: this.keyPickupLocationLatitude, lng: this.keyPickupLocationLongitude };
    return isNumber(res.lat) && isNumber(res.lng) ? res as LatLng : null;
  }

  set keyPickupLocation(val: LatLng | null) {
    if (val) {
      this.keyPickupLocationLatitude = val.lat;
      this.keyPickupLocationLongitude = val.lng;
    } else {
      this.keyPickupLocationLatitude = null;
      this.keyPickupLocationLongitude = null;
    }
  }
}

export type LatLng = {
  lat: number;
  lng: number;
};

export class PropertyDescriptionModel {
  id = 0;
  propertyId = 0;
  cultureCode = "hr";
  propertyDescription = "";
  shortPropertyDescription = "";
  areaDescription = "";
  activitiesDescription = "";
  arrivalInstructions = "";
  houseRulesDescription = "";
  policiesDescription = "";

  constructor(propertyId: number) {
    this.propertyId = propertyId;
  }
}

export class PropertyPoiModel {
  id = 0;
  propertyId = 0;
  name = "";

  poiTypeId: number | null = null;
  distanceInMeters: number | null = null;
  walkingTimeInMinutes: number | null = null;
  drivingDistanceInMinutes: number | null = null;

  constructor(propertyId: number) {
    this.propertyId = propertyId;
  }
}

export class PropertyOtaModel {
  propertyId = 0;
  otaId = 0;
  otaName = '';
  statusId: number | null = null;

  commissionPercent = 0;
  ownerPaysOTACommission = false;
  paymentCollectByOTA = false;
  paymentCollectCommission = 0;
  ratePlanId: number | null = null;
  accomodationUnitTypeId: number | null = null;

  constructor(propertyId: number) {
    this.propertyId = propertyId;
  }
}

export enum PropertyOtaStatusEnum {
  ActivationPending = 1,
  Active = 2,
  DeactivationPending = 3,
  Deactivated = 4
}

export class AccomodationUnitForOTASetupModel {
  id = 0;
  name = "";
  kroatiDeId = 0 as number | null;
}

export class AccomodationUnitTypeForOTASetupModel {
  id = 0;
  name = "";
  atraveoId = "";
}

export class KroatiDeSyncResult {
  isSuccess = true;
  errors = [] as KroatiDeSyncValidationModel[];
}

export class KroatiDeSyncValidationModel {
  unitTypeName = "";
  officialRateName = "";
  errorTextResourceKey = "";
}

export class AccomodationUnitTypeUnitOwners {
  id = 0;
  fullName = "";
}

export class OwnersAddOnServices {
  id = 0;
  name = "";
}

export class OfficialRateExportForOwners {
  propertyId = 0 as number | undefined;
  accUnitTypeId = 0 as number | undefined;
  ownerIds = "";
  ownerAddOnServiceIds = "";
  languageCode = "";
  specialNote = "";
  saveSpecialNote = true;
  isLandscape = true;
  exportType = 0 as OfficialRatesExportType;
  year = 0;
}